import { type ReactNode, useCallback, useEffect, useState } from 'react';

/**
 * Gemeinsame Bausteine der Auswertungs-Sektionen. Die KPI-Kacheln und Balken nutzen bewusst die
 * global geladenen Klassen aus der Vertriebs-Auswertung (.sales-controlling*, .sales-bar*), damit
 * jede Domäne gleich aussieht -- plus ein paar eigene Grid-Klassen in auswertung.css.
 */

export function euro0(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}
export function num(value: number): string {
  return value.toLocaleString('de-DE');
}
export function pct(value: number): string {
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
}
function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Auswertung konnte nicht geladen werden.';
}

/** Lädt Daten einmal (und auf Wunsch erneut). `load` muss der Aufrufer per useCallback stabil halten. */
export function useAsyncData<T>(load: () => Promise<T>): {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    load()
      .then((result) => setData(result))
      .catch((cause) => setError(messageOf(cause)))
      .finally(() => setLoading(false));
  }, [load]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload };
}

export function SectionShell({ loading, error, onReload, children }: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly onReload: () => void;
  readonly children: ReactNode;
}) {
  if (loading) return <div className="sales-controlling__loading">Auswertung wird berechnet …</div>;
  if (error) {
    return (
      <div className="sales-error" role="alert">
        <span>{error}</span>
        <button type="button" onClick={onReload}>Neu laden</button>
      </div>
    );
  }
  return <>{children}</>;
}

export function KpiGrid({ children }: { readonly children: ReactNode }) {
  return <div className="auswertung__kpis">{children}</div>;
}

export function Kpi({ label, value, hint }: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
}) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint != null ? <small>{hint}</small> : null}
    </article>
  );
}

export function Panel({ title, action, children }: {
  readonly title: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="sales-controlling__panel">
      <div className="auswertung__panelhead"><h3>{title}</h3>{action}</div>
      {children}
    </section>
  );
}

export interface BarRow {
  readonly key: string;
  readonly label: string;
  /** Kleine Zahl rechts neben dem Label (z. B. Anzahl). */
  readonly badge?: number;
  /** Steuert Balkenbreite; wird rechts über `format` angezeigt. */
  readonly value: number;
}

export function Bars({ rows, emptyLabel, format = euro0 }: {
  readonly rows: readonly BarRow[];
  readonly emptyLabel: string;
  readonly format?: (value: number) => string;
}) {
  if (rows.length === 0) return <p className="sales-controlling__empty">{emptyLabel}</p>;
  const max = Math.max(1, ...rows.map((row) => Math.abs(row.value)));
  return (
    <>
      {rows.map((row) => (
        <div className="sales-bar" key={row.key}>
          <span className="sales-bar__label">{row.label}{row.badge != null ? <em>{row.badge}</em> : null}</span>
          <span className="sales-bar__track"><span className="sales-bar__fill" style={{ width: `${Math.round((Math.abs(row.value) / max) * 100)}%` }} /></span>
          <span className="sales-bar__value">{format(row.value)}</span>
        </div>
      ))}
    </>
  );
}
